import { call, put, select, takeEvery } from 'redux-saga/effects'
import { handleApiError } from '../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import { getAuthenticationJwtSelector } from '../../login/selectors/getAuthenticationJwt.selector'
import { PulsaraConstants } from '../store/pulsara.constants'
import { PulsaraService } from '../services/pulsara.service'
import {
  ISelectedAttachment,
  IPulsaraAttachmentImageRequest,
  IPulsaraAttachmentImageResponse,
  IPulsaraAttachmentImage,
  IPulsaraAttachmentItem,
} from '../store/pulsara.types'
import { getPulsaraDetailsAttachmentList, getPulsaraDetailsAttachments, getSelectedAttachment } from '../selectors'
import i18n from '../../../i18n'
import { setPulsaraAttachments } from '../store/pulsara.actions'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import { isOffline } from '../../../utilities'

export function* fetchPulsaraAttachmentImage() {
  const selectedAttachment: ISelectedAttachment | null = yield select(getSelectedAttachment)
  const attachmentList: Array<IPulsaraAttachmentItem> = yield select(getPulsaraDetailsAttachmentList)
  const attachments: Array<IPulsaraAttachmentImage> = yield select(getPulsaraDetailsAttachments)

  if (!selectedAttachment) return

  const { attachmentId, pulsaraId } = selectedAttachment as ISelectedAttachment

  if (!pulsaraId || !attachmentId) return

  // Check if already loaded (cached)
  const alreadyLoaded = attachments.some((att) => att.id === attachmentId && att.blobData !== '')
  if (alreadyLoaded) return

  if (isOffline()) {
    const newAttachments = [...(attachments || [])]
    const index = newAttachments.findIndex((att) => att.id === attachmentId)
    if (index !== -1) {
      newAttachments[index] = { ...newAttachments[index], isUnavailable: true, blobData: '' }
    } else {
      const listItem = attachmentList?.find((att) => att.id === attachmentId)
      if (listItem) {
        const offlineAttachment: IPulsaraAttachmentImage = {
          id: listItem.id,
          pulsaraId: listItem.pulsaraId,
          name: listItem.name,
          category: listItem.category,
          mime: listItem.mime,
          size: null,
          fileExtension: listItem.name.split('.').pop()?.toLowerCase() ?? '',
          uploadedDt: '',
          blobData: '',
          isUnavailable: true,
        }
        newAttachments.push(offlineAttachment)
      }
    }
    yield put(setPulsaraAttachments(newAttachments))
    return
  }

  const authenticationJwt: string = yield select(getAuthenticationJwtSelector)

  try {
    const pulsaraService = new PulsaraService(authenticationJwt)

    const request: IPulsaraAttachmentImageRequest = {
      pulsaraId,
      attachmentId,
    }

    const response: IPulsaraAttachmentImageResponse = yield call(
      [pulsaraService, pulsaraService.fetchPulsaraAttachmentImage],
      request
    )

    if (response.status === 200) {
      const { attachment } = response.data

      if (attachment) {
        const listItem = attachmentList?.find((att) => att.id === attachmentId)
        const formattedAttachment: IPulsaraAttachmentImage = {
          ...attachment,
          mime: listItem?.mime || 'image/png',
          fileExtension: attachment.name.split('.').pop()?.toLowerCase() || '',
          category: listItem?.category || '',
          pulsaraId: listItem?.pulsaraId as number,
          isUnavailable: false,
        }

        const newAttachments = [...(attachments || [])]
        const index = newAttachments.findIndex((att) => att.id === attachmentId)
        if (index !== -1) {
          newAttachments[index] = formattedAttachment
        } else {
          newAttachments.push(formattedAttachment)
        }
        yield put(setPulsaraAttachments(newAttachments))
      }
    } else {
      response.failure = true
      if (response.messages?.length) {
        const index = response.messages.findIndex((x) => x.status === response.status)
        response.responseMessage = response.messages[index]?.message
      } else {
        response.responseMessage = i18n.t('pulsara.errors.fetchAttachmentImage')
      }
      yield put(handleApiError(response))
    }
  } catch (e: unknown) {
    const cause = e instanceof Error ? e.message : 'unknown'
    const errorResponse = {
      status: null,
      messages: [
        {
          status: null,
          display: [
            { id: 3, name: 'Alert' },
            { id: 1, name: 'Log' },
          ],
          message: i18n.t('pulsara.errors.fetchAttachmentImageFailed', { cause }),
        },
      ],
    }
    yield put(handleApiError(errorResponse))
  }
}

export function* fetchPulsaraAttachmentImageSaga() {
  yield takeEvery([PulsaraConstants.FETCH_PULSARA_ATTACHMENT_IMAGE], performWithMutexHOG, fetchPulsaraAttachmentImage)
}
